import React from 'react';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Link from '@material-ui/core/Link';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';
import { withTranslation, WithTranslation } from 'react-i18next';
import { AnchorBlock } from '../anchorBlock';
import { ActionLogEntry, CombinedLog, History } from '../../redux/reducers/api-types';
import DateUtils from '../../utils/dateUtils';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import StringUtils from '../../utils/stringUtils';
import {
    describeActionLogEntry,
    getFlightStatsEventDetails,
    isActionLogEntry,
    isTechnicalActionLog,
    resolveActionLogActorDisplay,
} from '../../utils/actionLogLevel';

interface ActionLogsProps extends WithTranslation {
    /** Pre-merged, newest-first timeline: legacy history and/or ecs-action-log dispatch events. */
    history?: CombinedLog[];
    withValues?: boolean;
    customFilter?: (h: History) => boolean;
    noEmpty?: boolean;
    allValues?: boolean;
}

const ActionLogs = (props: ActionLogsProps): JSX.Element => {
    const classes = useStyles();
    const [showTechnical, setShowTechnical] = React.useState(false);
    const { t, history, customFilter, noEmpty = true, allValues = false } = props;
    const allowedEntities = ['company', 'booking', 'reservation', 'category', 'driver'];

    // Pre-merged timeline from privapi (or a plain History[] from other views). Already sorted.
    const source: CombinedLog[] = history || [];
    if (source.length < 1) {
        return noEmpty ? null : <p>{t('noValue')}</p>;
    }
    // "Technical" dispatch rows (orchestrator/workflow internals, debug) are hidden unless toggled on.
    const hasTechnical = source.some((entry) => isActionLogEntry(entry) && isTechnicalActionLog(entry));

    // Migration view: when the timeline already has ecs-action-log (dispatch) events, prefer the
    // dispatch version and treat legacy as a *fallback* — a legacy row is dropped when a dispatch event
    // covers the same action (matched by action token + ~same time). Surviving legacy rows are the ones
    // the dispatch pipeline doesn't emit yet, so they're flagged with a red dot as "to migrate".
    const TWIN_WINDOW_MS = 5 * 60 * 1000;
    const hasDispatch = source.some((entry) => isActionLogEntry(entry));
    // Only dispatch events that are actually displayed count as twins — a hidden technical event
    // must not swallow the legacy row, or the action vanishes from the history altogether.
    const dispatchIndex = source
        .filter(isActionLogEntry)
        .filter((e) => showTechnical || !isTechnicalActionLog(e))
        .map((e) => ({ eventName: e.event_name, time: Date.parse(e.created_at) }));
    const legacyHasDispatchTwin = (h: History): boolean => {
        const tokens = (h.action || '').split(/\s+/);
        const time = (h.actionDate || 0) * 1000;
        return dispatchIndex.some((d) => tokens.includes(d.eventName) && Math.abs(d.time - time) <= TWIN_WINDOW_MS);
    };

    // Helper function to replace all matches in a string
    const replaceAll = (find: string[], replace: string, str: string): string =>
        find.reduce((result, item) => result.replace(new RegExp(item, 'g'), replace), str);

    // Helper function to replace all matches in a string and format JSON
    const formatJsonValue = (value: string | null | undefined): string =>
        value ? replaceAll(['{', '}'], '', JSON.stringify(JSON.parse(value), null, 2)) : '';

    const highlightDateDetails = (text: string): React.ReactNode => {
        const dateRegex = text.split(' ');
        if (dateRegex.length < 2) {
            return <span>{text}</span>;
        }
        // Highlight the date part (second part of the string)
        return (
            <span>
                {dateRegex[0]} <b>{dateRegex[1]}</b>
            </span>
        );
    };

    const renderHistoryRow = (
        { action, author, entity, id, history: subHistory, description, actionDate, pk }: History,
        index: number,
        migrationDot: boolean,
    ): JSX.Element => {
        const { id: authorId, class: authorClass } = author || {};
        const additionalInfo =
            subHistory?.oldValue?.includes('deletionDate') || subHistory?.newValue?.includes('deletionDate')
                ? ' (Suppression/Réactivation)'
                : '';
        const normalizedDescription = `${description}${additionalInfo}`;
        return (
            <Accordion key={`${pk}-${index}`}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel-${authorId}`}
                    id={`panel-${authorId}`}
                >
                    <div>
                        {migrationDot && (
                            <span className={classes.migrationDot} title="Log hérité (à migrer vers ecs-action-log)">
                                ●
                            </span>
                        )}
                        {StringUtils.userFullname(author)} {normalizedDescription} -{' '}
                        {highlightDateDetails(DateUtils.tsToDate(actionDate))}
                    </div>
                </AccordionSummary>
                <AccordionDetails className={classes.flexColumn}>
                    {authorClass !== 'admin' && (
                        <Link color="secondary" component={RouterLink} to={`/${authorClass}s/${authorId}`}>
                            {StringUtils.userFullname(author)}
                        </Link>
                    )}
                    {allowedEntities.includes(entity) && id && (
                        <Link
                            color="secondary"
                            component={RouterLink}
                            to={`/${entity === 'category' ? 'partner' : entity}s/${id}`}
                        >
                            {t('entity.' + entity)} {id}
                        </Link>
                    )}
                    <div>{`${t('actions.title')} : ${action
                        .split(' ')
                        .map((act) => t('actions.' + act, { defaultValue: act }))
                        ?.join(', ')}`}</div>
                    {(subHistory?.oldValue || subHistory?.newValue) && (
                        <div className={classes.flexRow}>
                            <span className={classes.flexWrap}>
                                <u>{t('newValue')}:</u>
                                <br />
                                <div>
                                    <pre>{formatJsonValue(subHistory?.newValue)}</pre>
                                </div>
                            </span>
                            <span className={classes.flexWrap} style={{ marginLeft: 8 }}>
                                <u>{t('oldValue')}:</u>
                                <br />
                                <div>
                                    <pre>{formatJsonValue(subHistory?.oldValue)}</pre>
                                </div>
                            </span>
                        </div>
                    )}
                </AccordionDetails>
            </Accordion>
        );
    };

    const renderActionLogRow = (entry: ActionLogEntry, index: number): JSX.Element => {
        // The human author (if any) is substituted into the description's role tag by
        // describeActionLogEntry — so we don't prefix it again. When there's no human and the event
        // comes from the dispatch engine, prefix a clear "engine · execution id" label instead.
        const actorDisplay = resolveActionLogActorDisplay(entry.author, entry.payload);
        const isEngine = entry.source === 'orchestrator' || entry.source === 'workflow';
        const enginePrefix =
            !actorDisplay && isEngine
                ? `${entry.workflow_name || 'Dispatch auto'}${entry.process_id ? ` · ${entry.process_id.slice(0, 8)}` : ''} — `
                : '';
        const description = describeActionLogEntry(entry, actorDisplay);
        const technical = isTechnicalActionLog(entry);
        return (
            <Accordion key={`actionlog-${entry.id}-${index}`}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`actionlog-panel-${entry.id}`}
                    id={`actionlog-panel-${entry.id}`}
                >
                    <div className={technical ? classes.technicalRow : undefined}>
                        {enginePrefix}
                        {description} - {highlightDateDetails(DateUtils.tsToDate(entry.created_at as any))}
                    </div>
                </AccordionSummary>
                <AccordionDetails className={classes.flexColumn}>
                    {/* FlightStats events: readable label/value lines instead of raw JSON (read-only). */}
                    {getFlightStatsEventDetails(entry.event_name, entry.payload).map(({ label, value }) => (
                        <div key={label}>
                            <u>{label}</u> : {value}
                        </div>
                    ))}
                    {/* Raw payload only for technical rows (opt-in via the toggle). */}
                    {technical && (
                        <div>
                            <pre>{JSON.stringify(entry.payload, null, 2)}</pre>
                        </div>
                    )}
                </AccordionDetails>
            </Accordion>
        );
    };

    // Single pass over the pre-merged timeline (order preserved — privapi already sorted newest-first).
    // History rows keep the legacy value filter + de-dup-insert; dispatch rows render with a meaningful
    // description, and "technical" ones are hidden unless the toggle is on.
    // Dedup: dispatch wins, legacy is fallback — a legacy row with a dispatch twin is dropped; the ones
    // that survive (no dispatch equivalent) get a red "to migrate" dot.
    let insertDone = false;
    const items: JSX.Element[] = [];
    source.forEach((entry, index) => {
        if (isActionLogEntry(entry)) {
            if (!isTechnicalActionLog(entry) || showTechnical) {
                items.push(renderActionLogRow(entry, index));
            }
            return;
        }
        if (legacyHasDispatchTwin(entry)) {
            return; // dispatch version covers this action — legacy is only a fallback
        }
        const passesValues = allValues
            ? true
            : (!entry.history?.oldValue && !entry.history?.newValue) ||
              entry.history?.oldValue?.includes('deletionDate') ||
              entry.history?.newValue?.includes('deletionDate');
        if (!passesValues || (customFilter && !customFilter(entry))) {
            return;
        }
        if (entry.action === 'insert') {
            if (insertDone) {
                return;
            }
            insertDone = true;
        }
        items.push(renderHistoryRow(entry, index, hasDispatch));
    });

    // Discreet toggle (right of the "Historique" title) to reveal the technical dispatch rows.
    const technicalToggle = hasTechnical ? (
        <FormControlLabel
            control={
                <Switch
                    size="small"
                    color="primary"
                    checked={showTechnical}
                    onChange={(e) => setShowTechnical(e.target.checked)}
                />
            }
            label={
                <span style={{ fontSize: 12, color: '#9e9e9e' }}>
                    {t('showTechnical', { defaultValue: 'Logs techniques' })}
                </span>
            }
            style={{ margin: 0 }}
        />
    ) : undefined;

    return (
        <>
            {(items.length > 0 || hasTechnical) && (
                <AnchorBlock
                    id="history"
                    title={t('history')}
                    items={items}
                    noValue={t('noValue')}
                    titleAction={technicalToggle}
                />
            )}
        </>
    );
};

//=====================================//
//=============== Style ===============//
//=====================================//

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        flexWrap: {
            flexWrap: 'wrap',
            flex: 1,
        },
        flexRow: {
            display: 'flex',
            flexDirection: 'row',
            marginTop: 8,
        },
        flexColumn: { flexDirection: 'column' },
        /** Technical dispatch rows: dimmer than normal history for a discreet, low-signal look. */
        technicalRow: { color: '#bdbdbd' },
        /** Tiny red bullet flagging a legacy row not yet emitted by the dispatch pipeline (to migrate). */
        migrationDot: { color: '#e53935', fontSize: 9, marginRight: 6, verticalAlign: 'middle' },
    }),
);

export default withTranslation('action_log')(ActionLogs);
